/**
 * RTWO Native Currency Engine — Emission Schedule Service
 */

import type { CurrencyId, RTWOEmissionSchedule, ScheduleId } from '../domain-models';
import type { EmissionScheduleResult, IEmissionScheduleService } from '../service-interfaces';
import type { NATVRepositories } from '../repositories/natv-repositories';
import type { LedgerAdapterRegistry } from '../registry/adapter-registries';
import { NATVInternalEventTypes } from '../events/natv-event-bus';
import { BaseNATVService, type NATVServiceContext } from './base-natv-service';

export class EmissionScheduleService extends BaseNATVService implements IEmissionScheduleService {
  readonly serviceId = 'natv-emission-schedule-001';
  readonly serviceName = 'Emission Schedule Service';
  readonly description =
    'Emission schedule service — placeholder, requires Genesis Configuration. No fake emission amounts or schedules.';
  readonly integrations = [
    'Core Engine',
    'Ledger Engine',
    'Staking/Rewards (future)',
    'Governance (future)',
  ];
  readonly requiresGenesisConfiguration = true;
  readonly requiresLedgerConnection = true;

  constructor(
    context: NATVServiceContext,
    private readonly repositories: NATVRepositories,
    private readonly ledgerRegistry: LedgerAdapterRegistry
  ) {
    super(context);
  }

  async initialize(): Promise<void> {
    this.status = 'waiting_for_genesis_configuration';
  }

  async configureEmissionSchedule(
    currencyId: CurrencyId,
    emissionType: string,
    emissionReference: string,
    startReference: string,
    endReference: string
  ): Promise<EmissionScheduleResult> {
    if (!this.ledgerRegistry.isConfigured()) {
      return {
        success: false,
        scheduleId: null,
        error:
          'Emission schedule configuration requires Genesis Configuration — Emission Schedule Not Configured',
        isPlaceholder: true,
        source: 'Native Currency Engine — Emission Schedule Not Configured',
      };
    }

    const adapterResult = await this.ledgerRegistry
      .getActiveAdapter()
      .configureEmissionSchedule(
        currencyId,
        emissionType,
        emissionReference,
        startReference,
        endReference
      );

    if (adapterResult?.schedule) {
      this.repositories.emissionSchedules.save(adapterResult.schedule);
      this.context.eventBus.emit(NATVInternalEventTypes.EMISSION_SCHEDULE_CONFIGURED, {
        scheduleId: adapterResult.schedule.scheduleId,
        currencyId,
      });
    }

    return {
      success: false,
      scheduleId: null,
      error:
        'Emission schedule configuration requires Genesis Configuration — Emission Schedule Not Configured',
      isPlaceholder: true,
      source: 'Native Currency Engine — Emission Schedule Not Configured',
    };
  }

  getAllEmissionSchedules(): RTWOEmissionSchedule[] {
    if (!this.ledgerRegistry.isConfigured()) return [];
    return this.repositories.emissionSchedules.findAll();
  }

  getEmissionSchedulesByCurrencyId(currencyId: CurrencyId): RTWOEmissionSchedule[] {
    if (!this.ledgerRegistry.isConfigured()) return [];
    return this.repositories.emissionSchedules.findByCurrencyId(currencyId);
  }

  getEmissionSchedule(scheduleId: ScheduleId): RTWOEmissionSchedule | null {
    if (!this.ledgerRegistry.isConfigured()) return null;
    return this.repositories.emissionSchedules.findById(scheduleId);
  }
}
